import { parseBahtToSatang } from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { ProductSelect } from '../../components/ProductSelect';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import { listBranches, listProducts } from '../../data/fetchers';
import { addStockMovement } from '../../data/mutators';
import { fmtAmount, fmtDate } from '../../lib/format';
import { visibleBranchIds } from '../../lib/permissions';
import {
  listTransfers,
  receiveTransfer,
  type TransferView,
} from '../../lib/transfers';
import { useBranchScope } from '../../lib/useBranchScope';
import { useSession } from '../../stores/session';
import { StockHeader } from './StockHeader';

const REASONS = [
  'รับจากซัพพลายเออร์',
  'ตรวจนับพบเพิ่ม',
  'รับคืนจากลูกค้า',
  'อื่น ๆ — ระบุ',
];

const errorMessageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

interface Line {
  productId: string;
  unit: string;
  qty: number;
  purchaseCost: number;
  searchQuery?: string;
}

export function GoodsReceiveView() {
  const qc = useQueryClient();
  const { scope } = useBranchScope();
  const user = useSession((s) => s.user);
  const [tab, setTab] = useState<'transfer' | 'manual'>('transfer');
  const { data: transfers = [] } = useQuery({
    queryKey: ['transfers', null],
    queryFn: () => listTransfers({ branchId: null }),
  });
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const shortOf = (b: string) => branches.find((x) => x.id === b)?.short ?? b;
  const allowed = user
    ? visibleBranchIds(
        user,
        branches.map((b) => b.id),
      )
    : [];
  const visible = branches.filter((b) => allowed.includes(b.id));
  // Only transfers whose destination the current user is allowed to receive into.
  const pending = transfers.filter(
    (t) =>
      t.status === 'sent' &&
      allowed.includes(t.toBranchId) &&
      (!scope.branchId || t.toBranchId === scope.branchId),
  );

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['stock'] });
    void qc.invalidateQueries({ queryKey: ['transfers'] });
    void qc.invalidateQueries({ queryKey: ['movements'] });
    void qc.invalidateQueries({ queryKey: ['products'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const receiveRequestIds = useRef(new Map<string, string>());
  const receiveInFlight = useRef(new Set<string>());
  const receiveMut = useMutation({
    mutationKey: ['transfer-receive'],
    meta: { errorRetry: true },
    mutationFn: ({ id, requestId }: { id: string; requestId: string }) =>
      receiveTransfer(id, requestId),
    onSuccess: (_transfer, variables) => {
      receiveRequestIds.current.delete(variables.id);
      invalidateAll();
      setSelectedTransfer(null);
    },
    onSettled: (_transfer, _error, variables) => {
      receiveInFlight.current.delete(variables.id);
    },
  });
  const startReceive = (id: string) => {
    if (receiveInFlight.current.has(id)) return;
    receiveInFlight.current.add(id);
    const requestId = receiveRequestIds.current.get(id) ?? crypto.randomUUID();
    receiveRequestIds.current.set(id, requestId);
    receiveMut.mutate({ id, requestId });
  };
  const [selectedTransfer, setSelectedTransfer] = useState<TransferView | null>(
    null,
  );

  // Manual receive form
  const [branchId, setBranchId] = useState('');
  const [reason, setReason] = useState(REASONS[0]);
  const [otherReason, setOtherReason] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<Line[]>([
    { productId: '', unit: '', qty: 1, purchaseCost: 0, searchQuery: '' },
  ]);

  // Scanner & Barcode states
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [barcodeQuery, setBarcodeQuery] = useState('');
  const [scanFeedback, setScanFeedback] = useState('');

  const playBeep = () => {
    try {
      const AudioCtx =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1000, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
    } catch (e) {
      console.warn('Audio beep failed', e);
    }
  };

  const handleBarcodeSearch = (code: string) => {
    let foundProduct = null;
    let foundUnit = '';
    let foundCost = 0;
    for (const p of products) {
      for (const u of p.units) {
        if (u.barcode === code.trim()) {
          foundProduct = p;
          foundUnit = u.unit;
          const defaultCost = p.cost ?? 0;
          foundCost = Math.round(defaultCost * u.factor); // satang (defaultCost is satang)
          break;
        }
      }
      if (foundProduct) break;
    }

    if (foundProduct) {
      playBeep();
      // Find if there is an empty line to populate, otherwise append a new line
      const emptyIdx = lines.findIndex((l) => !l.productId);
      if (emptyIdx !== -1) {
        setLine(emptyIdx, {
          productId: foundProduct.id,
          unit: foundUnit,
          qty: 1,
          purchaseCost: foundCost,
        });
      } else {
        setLines((ls) => [
          ...ls,
          {
            productId: foundProduct!.id,
            unit: foundUnit,
            qty: 1,
            purchaseCost: foundCost,
          },
        ]);
      }
      setScanFeedback(`✓ เพิ่มสินค้า: ${foundProduct.name} (${foundUnit})`);
      setBarcodeQuery('');
      setTimeout(() => setScanFeedback(''), 5000);
      return true;
    } else {
      setScanFeedback(`❌ ไม่พบบาร์โค้ดรหัส: "${code}"`);
      setTimeout(() => setScanFeedback(''), 5000);
      return false;
    }
  };

  // Camera scanner hook
  useEffect(() => {
    if (!isScanning) return;

    let stream: MediaStream | null = null;
    let scanInterval: any = null;

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current
            .play()
            .catch((e) => console.warn('Video play error', e));
        }

        if ('BarcodeDetector' in window) {
          const detector = new (window as any).BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'qr_code', 'code_128'],
          });

          scanInterval = setInterval(async () => {
            if (
              videoRef.current &&
              videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA
            ) {
              try {
                const codes = await detector.detect(videoRef.current);
                if (codes.length > 0) {
                  const raw = codes[0].rawValue;
                  handleBarcodeSearch(raw);
                  setIsScanning(false);
                }
              } catch (e) {
                console.error('Barcode detection error', e);
              }
            }
          }, 400);
        }
      })
      .catch((err) => {
        console.error('Camera access error', err);
        setCameraError(
          'ไม่สามารถเข้าถึงกล้องถ่ายรูปได้ โปรดตรวจสอบสิทธิ์การเข้าใช้งานกล้องในเบราว์เซอร์ของคุณ',
        );
      });

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (scanInterval) {
        clearInterval(scanInterval);
      }
    };
  }, [isScanning]);
  const setLine = (i: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const productOf = (id: string) => products.find((p) => p.id === id) ?? null;

  // Selecting a product resets the line's unit and default cost!
  const setLineProduct = (i: number, id: string) => {
    const p = productOf(id);
    const firstUnit = p?.units[0];
    const defaultCost = p?.cost ?? 0;
    const costForUnit = firstUnit
      ? defaultCost * firstUnit.factor
      : defaultCost;
    setLine(i, {
      productId: id,
      unit: firstUnit?.unit ?? '',
      purchaseCost: Math.round(costForUnit),
      searchQuery: p ? p.name : '',
    });
  };

  const removeLine = (i: number) => {
    setLines((ls) => ls.filter((_, j) => j !== i));
  };

  const manualMut = useMutation({
    mutationFn: async (payload: {
      branchId: string;
      reason: string;
      note: string;
      lines: Line[];
    }) => {
      for (const l of payload.lines) {
        const p = productOf(l.productId);

        await addStockMovement({
          branchId: payload.branchId,
          productId: l.productId,
          type: 'receive',
          qty: l.qty,
          reason: payload.reason,
          note: payload.note,
          unitCost: l.purchaseCost,
          unitId: p?.units.find((u) => u.unit === l.unit)?.id,
          requestId: crypto.randomUUID(),
        });
      }
    },
    onSuccess: () => {
      invalidateAll();
      setLines([{ productId: '', unit: '', qty: 1, purchaseCost: 0 }]);
    },
  });

  const onManualSubmit = (e: FormEvent) => {
    e.preventDefault();
    const valid = lines.filter((l) => l.productId && l.unit && l.qty > 0);
    if (!branchId || valid.length === 0) return;
    const finalReason = reason.startsWith('อื่น')
      ? otherReason || 'อื่น ๆ'
      : reason;
    manualMut.mutate({ branchId, reason: finalReason, note, lines: valid });
  };

  const uniqueProductCount = lines.filter((l) => l.productId).length;
  const totalQty = lines.reduce((acc, l) => acc + (l.productId ? l.qty : 0), 0);
  const totalCost = lines.reduce(
    (acc, l) => acc + (l.productId ? l.qty * l.purchaseCost : 0),
    0,
  );

  return (
    <div className="bo-fade">
      <StockHeader
        title="รับเข้าสินค้า"
        subtitle="รับโอนจากสาขา หรือรับเข้าด้วยตนเอง"
        status={{
          label:
            tab === 'transfer'
              ? `${pending.length} ใบโอนรอรับ`
              : `${uniqueProductCount} รายการในใบรับเข้า`,
          tone:
            tab === 'transfer' && pending.length > 0
              ? 'warn'
              : uniqueProductCount > 0
                ? 'ok'
                : 'neutral',
        }}
      />
      <div className="mb-6 flex flex-wrap gap-2 rounded-card border border-line bg-surface p-3">
        <button
          onClick={() => setTab('transfer')}
          className={`h-9 px-5 rounded-btn text-body transition-colors ${tab === 'transfer' ? 'bg-primary text-white font-bold shadow-sm' : 'bg-surface border border-line text-ink-2 hover:bg-bg-warm'}`}
        >
          รับโอนจากสาขา
        </button>
        <button
          onClick={() => setTab('manual')}
          className={`h-9 px-5 rounded-btn text-body transition-colors ${tab === 'manual' ? 'bg-primary text-white font-bold shadow-sm' : 'bg-surface border border-line text-ink-2 hover:bg-bg-warm'}`}
        >
          รับเข้า manual
        </button>
      </div>

      {tab === 'transfer' ? (
        <div className="flex flex-col gap-6">
          <Card title={`ใบโอนรอรับ (${pending.length})`}>
            {pending.length === 0 ? (
              <div className="text-ink-3 text-body py-8 text-center bg-surface-2/20 rounded-btn border border-dashed border-line-2">
                ไม่มีใบโอนรอรับในขณะนี้
              </div>
            ) : (
              <DataTable
                pageSize={Number.MAX_SAFE_INTEGER}
                mobileCards
                data={pending}
                onRowClick={setSelectedTransfer}
                columns={[
                  { accessorKey: 'referenceNo', header: 'เลขที่' },
                  {
                    id: 'branches',
                    header: 'จาก → ไป',
                    cell: ({ row }) =>
                      `${shortOf(row.original.fromBranchId)} → ${shortOf(row.original.toBranchId)}`,
                  },
                  {
                    id: 'date',
                    header: 'วันที่ส่งโอน',
                    cell: ({ row }) => fmtDate(row.original.createdAt),
                  },
                ]}
              />
            )}
          </Card>

          {selectedTransfer && (
            <Card
              title={
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2">
                  <span>
                    รายการสินค้าในใบโอน #{selectedTransfer.referenceNo}
                  </span>
                  <div className="flex gap-2">
                    <Btn
                      kind="default"
                      size="sm"
                      onClick={() => setSelectedTransfer(null)}
                    >
                      ซ่อน
                    </Btn>
                    <Btn
                      kind="primary"
                      size="sm"
                      onClick={() => startReceive(selectedTransfer.id)}
                      disabled={receiveMut.isPending}
                    >
                      {receiveMut.isPending
                        ? 'กำลังรับเข้า...'
                        : 'รับสินค้าทั้งหมดเข้าคลัง'}
                    </Btn>
                  </div>
                </div>
              }
            >
              <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4 bg-bg-warm/50 px-4 py-3 rounded-btn border border-line-2 text-body">
                <div>
                  <span className="text-ink-3">จากสาขา:</span>{' '}
                  <span className="font-semibold text-ink">
                    {shortOf(selectedTransfer.fromBranchId)}
                  </span>
                </div>
                <div>
                  <span className="text-ink-3">ไปยังสาขา:</span>{' '}
                  <span className="font-semibold text-ink">
                    {shortOf(selectedTransfer.toBranchId)}
                  </span>
                </div>
                <div>
                  <span className="text-ink-3">วันที่ส่งโอน:</span>{' '}
                  <span className="font-semibold text-ink">
                    {fmtDate(selectedTransfer.createdAt)}
                  </span>
                </div>
                <div className="sm:ml-auto">
                  <span className="text-ink-3">จำนวนสินค้า:</span>{' '}
                  <span className="font-bold text-primary">
                    {selectedTransfer.lines.length} รายการ
                  </span>
                </div>
              </div>

              <div className="max-h-[380px] overflow-auto rounded-btn border border-line">
                <table className="bo-table bo-table-mobile-cards">
                  <thead className="sticky top-0 bg-surface z-10 shadow-[0_1px_0_0_rgba(227,223,214,1)]">
                    <tr>
                      <th className="w-16">ลำดับ</th>
                      <th>สินค้า</th>
                      <th className="bo-th-num text-right pr-4">
                        จำนวนที่ส่งโอน
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTransfer.lines.map((l, idx) => {
                      const p = productOf(l.productId);
                      return (
                        <tr key={l.productId} className="hover:bg-bg-warm/30">
                          <td
                            data-label="ลำดับ"
                            className="w-16 num text-ink-3"
                          >
                            {idx + 1}
                          </td>
                          <td
                            data-label="สินค้า"
                            className="font-medium text-ink"
                          >
                            {p?.name ?? l.productId}
                          </td>
                          <td
                            data-label="จำนวนที่ส่งโอน"
                            className="bo-td-num num font-semibold text-right pr-4"
                          >
                            {l.qty} {p?.baseUnit ?? 'หน่วย'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      ) : (
        <form onSubmit={onManualSubmit} className="w-full flex flex-col gap-6">
          {/* Information Card */}
          <Card title="ข้อมูลการรับเข้า">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-semibold text-ink-2">
                  สาขาที่รับเข้า <span className="text-danger">*</span>
                </span>
                <Select
                  aria-label="สาขา"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  <option value="">— เลือกสาขา —</option>
                  {visible.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.short}
                    </option>
                  ))}
                </Select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-semibold text-ink-2">
                  เหตุผลการรับเข้า
                </span>
                <Select
                  aria-label="เหตุผล"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                >
                  {REASONS.map((r) => (
                    <option key={r}>{r}</option>
                  ))}
                </Select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-semibold text-ink-2">
                  หมายเหตุ
                </span>
                <Input
                  type="text"
                  aria-label="หมายเหตุ"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="ระบุหมายเหตุเพิ่มเติม (ถ้ามี)"
                />
              </label>

              {reason.startsWith('อื่น') && (
                <div className="md:col-span-3">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm-label font-semibold text-ink-2">
                      ระบุเหตุผลอื่นๆ <span className="text-danger">*</span>
                    </span>
                    <Input
                      aria-label="ระบุเหตุผล"
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      placeholder="โปรดระบุเหตุผล..."
                    />
                  </label>
                </div>
              )}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Left Column: Line Items Table */}
            <div className="lg:col-span-8 flex flex-col gap-4">
              {/* Barcode scan section */}
              <Card title="สแกนบาร์โค้ดสินค้า">
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-semibold text-ink-2">
                    ค้นหาด้วยบาร์โค้ด / ยิงสแกนด่วนเพื่อเพิ่มรายการ
                  </span>
                  <div className="grid grid-cols-2 gap-2 sm:flex">
                    <div className="relative col-span-2 sm:flex-1">
                      <Input
                        className="w-full pr-12"
                        type="text"
                        placeholder="กรอกหรือยิงรหัสบาร์โค้ดเพื่อเพิ่มเข้าใบรับเข้า..."
                        value={barcodeQuery}
                        onChange={(e) => setBarcodeQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (barcodeQuery) handleBarcodeSearch(barcodeQuery);
                          }
                        }}
                      />
                      {barcodeQuery && (
                        <button
                          type="button"
                          onClick={() => setBarcodeQuery('')}
                          className="absolute right-3 top-2.5 text-ink-3 hover:text-ink"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <Btn
                      type="button"
                      kind="default"
                      onClick={() => handleBarcodeSearch(barcodeQuery)}
                      disabled={!barcodeQuery}
                    >
                      ค้นหา
                    </Btn>
                    <Btn
                      type="button"
                      kind="primary"
                      className="bg-accent hover:bg-accent-dark text-white shadow-sm"
                      onClick={() => {
                        setIsScanning(true);
                        setCameraError('');
                      }}
                      icon={
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4v1m6 11h2m-6 0h-2v4m0-16v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                          />
                        </svg>
                      }
                    >
                      สแกนกล้อง
                    </Btn>
                  </div>
                  {scanFeedback && (
                    <div
                      className={`text-xs font-semibold px-3 py-2 rounded-btn mt-1 transition-all ${
                        scanFeedback.startsWith('✓')
                          ? 'bg-ok-light text-ok border border-ok/10'
                          : 'bg-danger-light text-danger border border-danger/10'
                      }`}
                    >
                      {scanFeedback}
                    </div>
                  )}
                </div>
              </Card>

              <Card title="รายการสินค้าที่รับเข้า">
                <div className="overflow-visible">
                  <table className="w-full border-collapse">
                    <thead className="hidden md:table-header-group">
                      <tr className="border-b border-line text-sm-label text-ink-3 uppercase font-bold">
                        <th className="pb-3 text-left pl-1">สินค้า</th>
                        <th className="pb-3 text-left w-24">จำนวน</th>
                        <th className="pb-3 text-left w-28">หน่วย</th>
                        <th className="pb-3 text-left w-40">ราคาทุน / หน่วย</th>
                        <th className="pb-3 text-right pr-1 w-12"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line-2">
                      {lines.map((l, i) => {
                        const p = productOf(l.productId);
                        const currentUnitInfo = p?.units.find(
                          (u) => u.unit === l.unit,
                        );
                        const factor = currentUnitInfo?.factor ?? 1;

                        return (
                          <tr
                            key={i}
                            className="grid grid-cols-12 md:table-row py-4 md:py-0 border-b border-line-2 md:border-none gap-x-2 gap-y-3 md:gap-0"
                          >
                            <td className="col-span-12 md:table-cell py-1.5 md:py-3 pr-0 md:pr-2 pl-1">
                              <span className="md:hidden block text-xs font-bold text-ink-3 mb-1">
                                สินค้า
                              </span>
                              <ProductSelect
                                products={products}
                                value={l.productId}
                                onChange={(id) => setLineProduct(i, id)}
                              />
                            </td>
                            <td className="col-span-3 md:table-cell py-1.5 md:py-3 pr-0 md:pr-2">
                              <span className="md:hidden block text-xs font-bold text-ink-3 mb-1">
                                จำนวน
                              </span>
                              <Input
                                aria-label={`จำนวนแถว ${i + 1}`}
                                type="number"
                                min={1}
                                value={l.qty}
                                onChange={(e) =>
                                  setLine(i, { qty: Number(e.target.value) })
                                }
                                placeholder="จำนวน"
                              />
                            </td>
                            <td className="col-span-3 md:table-cell py-1.5 md:py-3 pr-0 md:pr-2">
                              <span className="md:hidden block text-xs font-bold text-ink-3 mb-1">
                                หน่วย
                              </span>
                              <Select
                                aria-label={`หน่วยแถว ${i + 1}`}
                                value={l.unit}
                                onChange={(e) =>
                                  setLine(i, { unit: e.target.value })
                                }
                                disabled={!l.productId}
                              >
                                {(p?.units ?? []).map((u) => (
                                  <option key={u.unit} value={u.unit}>
                                    {u.unit}
                                  </option>
                                ))}
                              </Select>
                            </td>
                            <td className="col-span-5 md:table-cell py-1.5 md:py-3 pr-0 md:pr-2">
                              <span className="md:hidden block text-xs font-bold text-ink-3 mb-1">
                                ทุน / หน่วย
                              </span>
                              <div>
                                <div className="relative">
                                  <Input
                                    aria-label={`ราคาทุนแถว ${i + 1}`}
                                    disabled={!l.productId}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={l.purchaseCost / 100}
                                    onChange={(e) =>
                                      setLine(i, {
                                        purchaseCost: parseBahtToSatang(
                                          e.target.value,
                                        ),
                                      })
                                    }
                                    placeholder="0.00"
                                  />
                                  <span className="absolute right-3 top-[10px] text-xs text-ink-3 font-semibold">
                                    ฿
                                  </span>
                                </div>
                                {p && factor > 1 && l.purchaseCost > 0 && (
                                  <div className="text-sm-label text-ink-3 mt-1 pl-1">
                                    (คิดเป็น{' '}
                                    {fmtAmount(l.purchaseCost / factor)}/ชิ้น)
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="col-span-1 md:table-cell py-1.5 md:py-3 text-right pr-1 flex items-end justify-center pb-0.5">
                              <div className="flex justify-end pb-0.5">
                                <button
                                  type="button"
                                  onClick={() => removeLine(i)}
                                  disabled={lines.length === 1}
                                  className="h-10 w-10 inline-flex items-center justify-center rounded-btn text-ink-3 hover:text-danger hover:bg-danger/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-3 transition-colors"
                                  title="ลบรายการ"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 pt-4 border-t border-line-2 flex justify-start">
                  <Btn
                    kind="default"
                    size="sm"
                    type="button"
                    onClick={() =>
                      setLines((ls) => [
                        ...ls,
                        { productId: '', unit: '', qty: 1, purchaseCost: 0 },
                      ])
                    }
                  >
                    + เพิ่มรายการสินค้า
                  </Btn>
                </div>
              </Card>
            </div>

            {/* Right Column: Metadata & Summary Panel */}
            <div className="lg:col-span-4 flex flex-col gap-6">
              {/* Summary Invoice Card */}
              <Card title="สรุปรายการรับเข้า">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-center text-body">
                    <span className="text-ink-3">สินค้าที่รับเข้า:</span>
                    <span className="font-semibold text-ink">
                      {uniqueProductCount} รายการ
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-body">
                    <span className="text-ink-3">จำนวนรวมทั้งหมด:</span>
                    <span className="font-semibold text-ink">
                      {totalQty} หน่วย
                    </span>
                  </div>

                  <div className="border-t border-line-2 my-1"></div>

                  <div className="flex justify-between items-baseline">
                    <span className="text-ink font-semibold">
                      มูลค่ารวมโดยประมาณ:
                    </span>
                    <span className="text-h2 font-black text-primary num">
                      ฿{fmtAmount(totalCost)}
                    </span>
                  </div>

                  <Btn
                    kind="primary"
                    type="submit"
                    disabled={
                      !branchId ||
                      uniqueProductCount === 0 ||
                      manualMut.isPending
                    }
                    className="w-full h-11 text-body font-bold mt-2"
                  >
                    {manualMut.isPending
                      ? 'กำลังบันทึก...'
                      : 'บันทึกการรับเข้า'}
                  </Btn>
                  {!branchId && (
                    <p className="text-center text-xs font-semibold text-warn">
                      กรุณาเลือกสาขาที่รับเข้าก่อนบันทึก
                    </p>
                  )}
                  {branchId && uniqueProductCount === 0 && (
                    <p className="text-center text-xs font-semibold text-warn">
                      กรุณาเพิ่มสินค้าที่รับเข้าอย่างน้อย 1 รายการ
                    </p>
                  )}
                  {manualMut.error && (
                    <div
                      role="alert"
                      className="rounded-btn border border-danger/20 bg-danger-light px-3 py-2 text-sm font-semibold text-danger"
                    >
                      {errorMessageOf(manualMut.error)}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </form>
      )}

      {/* Camera scan fullscreen interface */}
      {isScanning && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="receive-scanner-title"
          aria-describedby="receive-scanner-description"
          className="fixed inset-0 z-50 isolate overflow-y-auto bg-neutral-950"
        >
          <style
            dangerouslySetInnerHTML={{
              __html: `
            @keyframes scanLaser {
              0% { top: 0%; }
              50% { top: 100%; }
              100% { top: 0%; }
            }
          `,
            }}
          />

          {/* Scanning Guide Overlay */}
          <div className="relative z-10 mx-auto flex min-h-dvh w-full max-w-lg flex-col gap-4 px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] text-center sm:px-6">
            <div className="flex w-full shrink-0 items-center justify-between gap-3 rounded-xl bg-neutral-900 p-3">
              <h2
                id="receive-scanner-title"
                className="text-h3 font-extrabold text-white"
              >
                สแกนบาร์โค้ดสินค้า
              </h2>
              <button
                type="button"
                aria-label="ปิดหน้าต่างสแกนบาร์โค้ด"
                onClick={() => setIsScanning(false)}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-neutral-950 text-xl transition-colors hover:bg-neutral-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
              >
                ✕
              </button>
            </div>

            {/* Scanning Target Box */}
            <div className="flex min-h-56 flex-1 items-center justify-center py-4">
              <div className="relative h-48 w-48 overflow-hidden rounded-xl border-2 border-white sm:h-64 sm:w-64">
                {/* Laser Line */}
                <div className="absolute left-0 right-0 h-0.5 bg-danger shadow-[0_0_8px_rgba(200,54,42,0.8)] animate-[scanLaser_2s_infinite]"></div>
              </div>
            </div>
            <div
              id="receive-scanner-description"
              className="rounded-xl bg-neutral-900 px-4 py-3 text-sm font-semibold leading-relaxed text-white"
            >
              วางบาร์โค้ดให้อยู่ในกรอบสี่เหลี่ยมเพื่อสแกนอัตโนมัติ
            </div>
            {/* Simulated Scanner Fallback */}
            {!('BarcodeDetector' in window) && !cameraError && (
              <div className="flex shrink-0 flex-col gap-3 rounded-xl border border-neutral-500 bg-neutral-900 p-4 text-left text-sm text-white">
                <div className="flex gap-2 items-start text-amber-300 font-bold">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span>
                    เบราว์เซอร์ไม่รองรับตัวตรวจจับบาร์โค้ดด่วนในกล้องสด
                  </span>
                </div>
                <span className="text-neutral-100">
                  คุณสามารถค้นหาด้วยการพิมพ์รหัสบาร์โค้ด
                  หรือทดสอบโดยการคลิกจำลองรายการสแกนดังต่อไปนี้:
                </span>
                <Select
                  aria-label="จำลองการสแกนสินค้า"
                  className="!border-neutral-400 !bg-neutral-800 !text-white [color-scheme:dark]"
                  wrapperClassName="text-white"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBarcodeSearch(e.target.value);
                      setIsScanning(false);
                    }
                  }}
                >
                  <option value="" className="bg-ink">
                    --- เลือกสินค้าเพื่อจำลองการสแกน ---
                  </option>
                  {products.map((p) =>
                    p.units.map((u) => (
                      <option
                        key={u.barcode}
                        value={u.barcode}
                        className="bg-ink"
                      >
                        {p.name} ({u.unit}) - {u.barcode}
                      </option>
                    )),
                  )}
                </Select>
              </div>
            )}
          </div>

          {/* Camera Video Feed */}
          <video
            ref={videoRef}
            className="absolute inset-0 z-0 w-full h-full object-cover brightness-[.65]"
            playsInline
            muted
          />

          {cameraError && (
            <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-6 text-center z-30">
              <span className="text-danger text-lg font-bold mb-2">
                เกิดข้อผิดพลาดในการโหลดกล้อง
              </span>
              <span className="text-white text-sm mb-6 max-w-sm">
                {cameraError}
              </span>
              <Btn kind="default" onClick={() => setIsScanning(false)}>
                ปิดหน้าต่างสแกน
              </Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
